import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings, uploadLogo } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import {
  Settings, Building, Shield, Receipt, Cloud, Loader2, Save, Copy, CheckCheck, Hash,
  Upload, Landmark, Percent, Link2
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const copyCompanyId = () => {
    if (!user?.company_id) return;
    navigator.clipboard.writeText(user.company_id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const data = await getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const result = await uploadLogo(file);
      updateField('logo_url', result.logo_url);
      toast.success('Logo hochgeladen');
    } catch {
      toast.error('Fehler beim Hochladen');
    } finally {
      setLogoUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Einstellungen</h1>
          <p className="text-slate-500">Systemkonfiguration</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={isSaving}
          className="bg-[#1a2f38] hover:bg-[#2a4048]"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Speichern
        </Button>
      </div>

      {/* Company ID — shown to admin so they can share it with waiters */}
      {user?.company_id && (
        <Card className="shadow-sm border-[#1a2f38]/20 bg-slate-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Hash className="w-4 h-4 text-[#1a2f38]" />
              Unternehmens-Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <code className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-[#1a2f38] font-mono text-sm tracking-widest select-all">
                {user.company_id}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={copyCompanyId}
                className="shrink-0 gap-1.5"
              >
                {copied ? <CheckCheck className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Kopiert' : 'Kopieren'}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Dieser Code wird beim POS-Login benötigt, damit Kellner das richtige Unternehmen auswählen.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Business Info */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="w-5 h-5" />
            Geschäftsdaten
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Firmenname</Label>
              <Input
                value={settings?.company_name || ''}
                onChange={(e) => updateField('company_name', e.target.value)}
                placeholder="FiscalPOS GmbH"
              />
            </div>
            <div>
              <Label>UID-Nummer</Label>
              <Input
                value={settings?.company_uid || ''}
                onChange={(e) => updateField('company_uid', e.target.value)}
                placeholder="ATU12345678"
              />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input
                value={settings?.company_address || ''}
                onChange={(e) => updateField('company_address', e.target.value)}
                placeholder="Musterstraße 1"
              />
            </div>
            <div>
              <Label>PLZ / Ort</Label>
              <Input
                value={settings?.company_city || ''}
                onChange={(e) => updateField('company_city', e.target.value)}
                placeholder="1010 Wien"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logo */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Firmenlogo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            {settings?.logo_url ? (
              <img
                src={settings.logo_url}
                alt="Logo"
                className="h-16 w-auto max-w-[160px] object-contain border rounded-lg p-2 bg-white"
              />
            ) : (
              <div className="h-16 w-32 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-slate-50">
                <span className="text-xs text-slate-400">Kein Logo</span>
              </div>
            )}
            <div>
              <label htmlFor="logo-upload">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={logoUploading}
                  onClick={() => document.getElementById('logo-upload').click()}
                >
                  {logoUploading
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Upload className="w-4 h-4 mr-2" />}
                  Logo hochladen
                </Button>
              </label>
              <input
                id="logo-upload"
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.svg"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <p className="text-xs text-slate-400 mt-1">PNG, JPG, SVG · max 2 MB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bankdaten + Kleinstunternehmer */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5" />
            Bankdaten &amp; Steuer
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Bank</Label>
              <Input
                value={settings?.bank_name || ''}
                onChange={(e) => updateField('bank_name', e.target.value)}
                placeholder="Erste Bank"
              />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input
                value={settings?.iban || ''}
                onChange={(e) => updateField('iban', e.target.value)}
                placeholder="AT60 1200 0006 2658 4800"
              />
            </div>
            <div>
              <Label>BIC</Label>
              <Input
                value={settings?.bic || ''}
                onChange={(e) => updateField('bic', e.target.value)}
                placeholder="GIBAATWW"
              />
            </div>
          </div>
          <Separator />
          <div className="flex items-center justify-between py-1">
            <div>
              <p className="font-medium flex items-center gap-1.5">
                <Percent className="w-4 h-4" />
                Kleinunternehmerregelung
              </p>
              <p className="text-sm text-slate-500">§6 Abs. 1 Z 27 UStG — MwSt. wird auf Belegen nicht ausgewiesen</p>
            </div>
            <Switch
              checked={settings?.kleinstunternehmer ?? false}
              onCheckedChange={(checked) => updateField('kleinstunternehmer', checked)}
            />
          </div>
          <Separator />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>FinanzOnline Teilnehmer-ID</Label>
              <Input
                value={settings?.finanz_online_tid || ''}
                onChange={(e) => updateField('finanz_online_tid', e.target.value)}
                placeholder="12345678"
              />
            </div>
            <div>
              <Label>FinanzOnline Benutzer-ID</Label>
              <Input
                value={settings?.finanz_online_uid || ''}
                onChange={(e) => updateField('finanz_online_uid', e.target.value)}
                placeholder="USER001"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Terminal Settings */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Terminal-Einstellungen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Terminal-ID</Label>
              <Input
                value={settings?.terminal_id || ''}
                onChange={(e) => updateField('terminal_id', e.target.value)}
                placeholder="POS_001"
              />
            </div>
            <div>
              <Label>Kassen-ID</Label>
              <Input
                value={settings?.kassen_id || ''}
                onChange={(e) => updateField('kassen_id', e.target.value)}
                placeholder="KASSE001"
              />
            </div>
            <div>
              <Label>Standard MwSt-Satz (%)</Label>
              <Input
                type="number"
                value={settings?.default_vat_rate || 10}
                onChange={(e) => updateField('default_vat_rate', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <Label>Währung</Label>
              <Input
                value={settings?.currency_symbol || '€'}
                onChange={(e) => updateField('currency_symbol', e.target.value)}
                disabled
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Workforce / Terminal Bridge
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">Zweites Supabase-Projekt</p>
                <p className="text-xs text-slate-500">
                  Verbindet POSS mit Reservierungen, Zeiterfassung und dem Companyapp-Terminal.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  settings?.workforce_bridge_status?.env_configured
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {settings?.workforce_bridge_status?.env_configured ? 'Server verbunden' : 'Server-Key fehlt'}
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  settings?.workforce_bridge_status?.location_configured
                    ? 'bg-sky-100 text-sky-700'
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {settings?.workforce_bridge_status?.location_configured ? 'Standort gesetzt' : 'Standort offen'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <p className="font-medium text-slate-800">Bridge aktivieren</p>
              <p className="text-sm text-slate-500">
                Aktiviert Reservierungs- und Zeiterfassungszugriffe im POSS.
              </p>
            </div>
            <Switch
              checked={settings?.workforce_bridge_enabled ?? false}
              onCheckedChange={(checked) => updateField('workforce_bridge_enabled', checked)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Projekt-Ref</Label>
              <Input
                value={settings?.workforce_project_ref || 'bhhhfdlwqhedxefudcdd'}
                onChange={(e) => updateField('workforce_project_ref', e.target.value)}
                placeholder="bhhhfdlwqhedxefudcdd"
              />
            </div>
            <div>
              <Label>Workforce Company ID</Label>
              <Input
                value={settings?.workforce_company_id || ''}
                onChange={(e) => updateField('workforce_company_id', e.target.value)}
                placeholder="Firma im zweiten Projekt"
              />
            </div>
            <div>
              <Label>Workforce Location ID</Label>
              <Input
                value={settings?.workforce_location_id || ''}
                onChange={(e) => updateField('workforce_location_id', e.target.value)}
                placeholder="Standort fuer Reservierungen / Zeiterfassung"
              />
            </div>
            <div>
              <Label>Workforce Terminal ID</Label>
              <Input
                value={settings?.workforce_terminal_id || ''}
                onChange={(e) => updateField('workforce_terminal_id', e.target.value)}
                placeholder="Optionales Terminal aus Companyapp"
              />
            </div>
          </div>

          <p className="text-xs text-slate-400">
            Server-seitig werden dafuer `WORKFORCE_SUPABASE_URL` und `WORKFORCE_SUPABASE_KEY` verwendet. Wenn keine URL gesetzt ist,
            wird automatisch `https://bhhhfdlwqhedxefudcdd.supabase.co` verwendet.
          </p>
        </CardContent>
      </Card>

      {/* Fiskaltrust / RKSV */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Fiskaleinstellungen (RKSV / fiskaltrust.at)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(settings?.ft_is_mock ?? true) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
              <span className="text-amber-600 shrink-0 text-base">⚠</span>
              <p className="text-sm text-amber-800">
                <strong>Mock-Modus aktiv.</strong> Signaturen sind nicht rechtskonform.
                Für Produktivbetrieb CashBox-Daten eintragen und Mock deaktivieren.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Mock-Modus (Testsignaturen)</p>
              <p className="text-sm text-slate-500">
                Deaktivieren für echte fiskaltrust.at Signaturen
              </p>
            </div>
            <Switch
              checked={settings?.ft_is_mock ?? true}
              onCheckedChange={(checked) => updateField('ft_is_mock', checked)}
            />
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>CashBox ID</Label>
              <Input
                value={settings?.ft_cashbox_id || ''}
                onChange={(e) => updateField('ft_cashbox_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                type="password"
              />
            </div>
            <div>
              <Label>PosSystem ID</Label>
              <Input
                value={settings?.ft_possystem_id || ''}
                onChange={(e) => updateField('ft_possystem_id', e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                type="password"
              />
            </div>
            <div>
              <Label>Access Token</Label>
              <Input
                value={settings?.ft_access_token || ''}
                onChange={(e) => updateField('ft_access_token', e.target.value)}
                placeholder="fiskaltrust Access Token"
                type="password"
              />
            </div>
          </div>

          <div>
            <Label>API URL</Label>
            <Input
              value={settings?.ft_api_url || 'https://possystem-api.fiskaltrust.eu/v2'}
              onChange={(e) => updateField('ft_api_url', e.target.value)}
            />
          </div>

          <p className="text-xs text-slate-400">
            Fuer Sandbox-Tests mit der aktuellen fiskaltrust POS System API
            {' '}
            <span className="font-mono">https://possystem-api-sandbox.fiskaltrust.eu/v2</span>
            {' '}
            verwenden. Wenn keine PosSystem ID hinterlegt ist, nutzt die Sandbox automatisch die
            Null-ID.
          </p>

          <p className="text-xs text-slate-400">
            Nach dem Speichern im Fiskaljournal unter{' '}
            <a href="/admin/basic/fiscal" className="underline hover:text-slate-600">
              Einstellungen → Fiskalierung
            </a>{' '}
            einen Startbeleg erstellen.
          </p>
        </CardContent>
      </Card>

      {/* Receipt Settings */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Beleg-Einstellungen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Beleg Kopfzeile</Label>
            <Input
              value={settings?.receipt_header || ''}
              onChange={(e) => updateField('receipt_header', e.target.value)}
              placeholder="Zusätzlicher Text oben"
            />
          </div>
          <div>
            <Label>Beleg Fußzeile</Label>
            <Input
              value={settings?.receipt_footer || ''}
              onChange={(e) => updateField('receipt_footer', e.target.value)}
              placeholder="Danke für Ihren Besuch!"
            />
          </div>
        </CardContent>
      </Card>

      {/* Offline Settings */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5" />
            Offline & Sync
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Offline-Modus aktiviert</p>
              <p className="text-sm text-slate-500">POS funktioniert ohne Internetverbindung</p>
            </div>
            <Switch
              checked={settings?.offline_mode_enabled ?? true}
              onCheckedChange={(checked) => updateField('offline_mode_enabled', checked)}
            />
          </div>
          <div>
            <Label>Sync-Intervall (Sekunden)</Label>
            <Input
              type="number"
              value={settings?.sync_interval_seconds || 30}
              onChange={(e) => updateField('sync_interval_seconds', parseInt(e.target.value))}
              min={10}
              max={300}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
